import { FC, useMemo, useState } from "react"
import cx from "classnames"
import { MdLogin as LoginIcon } from "react-icons/md"
import { RiDeleteBin6Line as BinIcon } from "react-icons/ri"
import { MdOutlineModeEdit as EditIcon } from "react-icons/md"
import Table from "@/components/Table"
import LoginEmailModal from "../LoginEmailModal"
import EditEmailModal from "../EditEmailModal"
import DeleteEmailModal from "../DeleteEmailModal"
import EmailAddressDetailsDrawer from "../EmailAddressDetailsDrawer"
import Chip from "@/components/Chip"
import EmailPlatformColumnValue from "@/components/EmailPlatformColumnValue"
import { toCapitalize } from "@/utils/strings"
import { useBreakpoints } from "@/hooks/useBreakpoints"
import { useUserAccess } from "@/hooks/useUserAccess"
import type { ColumnsType } from "antd/es/table"
import type { TablePaginationProps } from "@/redux/services/types"
import type { Campaign } from "@/types/entities/campaign"
import type { EmailAddress } from "@/types/entities/email-address"
import CheckCircleIcon from "@/components/icons/CheckCircleIcon"
import CancelIcon from "@/assets/cancel.svg"

type Props = {
  className?: string
  emails: EmailAddress[]
} & TablePaginationProps<TableDataType>

type TableDataType = {
  key: string
  email: string
  provider: string
  sender: string
  followUpQueue: number
  campaigns: Campaign[]
  isConnected: Boolean
  active: Boolean
  hasSender: boolean
}

const EmailAddressesTable: FC<Props> = ({
  className,
  emails,
  ...rest
}: Props) => {
  const { sm } = useBreakpoints()
  const [reloginMailId, setReloginMailId] = useState("")
  const [editedMailId, setEdittedMailId] = useState("")
  const [deletedMailId, setDeletedMailId] = useState("")
  const [selectedItemId, setSelectedItemId] = useState("")
  const { pageFullAccess } = useUserAccess({ pageName: "Email Addresses" })
  const dataSource = useMemo<TableDataType[]>(
    () =>
      emails.map(
        ({
          _id,
          sender,
          email,
          followUpQueue,
          campaigns,
          provider,
          isConnected,
          active,
        }) => ({
          key: _id,
          provider,
          active,
          isConnected,
          hasSender: Boolean(sender),
          sender: sender
            ? `${sender.firstName} ${sender.lastName}${
                sender.tag ? ` (${sender.tag})` : ""
              }`
            : "Unassigned",
          followUpQueue,
          email,
          campaigns,
        }),
      ),
    [emails],
  )
  const columns = useMemo<ColumnsType<TableDataType>>(() => {
    let resultCols: ColumnsType<TableDataType> = [
      {
        title: "Email Address",
        key: "email",
        dataIndex: "email",
        sorter: (a, b) => a.email.localeCompare(b.email),
        width: 300,
      },
      {
        title: "Provider",
        key: "provider",
        dataIndex: "provider",
        sorter: (a, b) => a.provider.localeCompare(b.provider),
        render: (value, { provider }) => (
          <EmailPlatformColumnValue
            provider={value}
            value={toCapitalize(provider)}
          />
        ),
        width: 150,
      },
      {
        title: "Connected",
        key: "isConnected",
        dataIndex: "isConnected",
        width: 180,
        // sorter: (a, b) => Number(a.active) - Number(b.active),
        render: (v) => (
          <>
            <span>{v ? "Connected" : "Disconnected"}</span>
            {v ? (
              <CheckCircleIcon className="ml-1.5 inline-block text-positive" />
            ) : (
              <CancelIcon className="ml-[8.5px] inline-block text-error w-5 h-5" />
            )}
          </>
        ),
      },
      {
        title: "Status",
        key: "active",
        dataIndex: "active",
        width: 180,
        // sorter: (a, b) => Number(a.active) - Number(b.active),
        render: (v) => (
          <>
            <span>{v ? "Active" : "Deleted"}</span>
            {v ? (
              <CheckCircleIcon className="ml-1.5 inline-block text-positive" />
            ) : (
              <CancelIcon className="ml-[8.5px] inline-block text-error w-5 h-5" />
            )}
          </>
        ),
      },
      {
        title: "Sender",
        key: "sender",
        dataIndex: "sender",
        sorter: (a, b) => a.sender.localeCompare(b.sender),
        width: 225,
      },
      {
        title: "Follow Up Queue",
        key: "followUpQueue",
        dataIndex: "followUpQueue",
        sorter: (a, b) => a.followUpQueue - b.followUpQueue,
        width: 175,
      },
      {
        title: "Campaigns",
        key: "campaigns",
        dataIndex: "campaigns",
        sorter: (a, b) => a.campaigns.length - b.campaigns.length,
        render: (_, { campaigns }) =>
          campaigns?.filter(Boolean).length ? (
            <div className="flex gap-1.5">
              {campaigns.slice(0, 1).map((campaign, i) => (
                <Chip className="hover:text-primary whitespace-nowrap" key={i}>
                  {campaign.name ?? JSON.stringify(campaign)}
                </Chip>
              ))}
              {campaigns.length > 1 && (
                <span className="text-sm leading-6 opacity-60">
                  +{campaigns.length - 1}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm leading-6 opacity-40">None</span>
          ),
        width: 150,
      },
    ]
    if (pageFullAccess) {
      resultCols = resultCols.concat({
        title: "",
        dataIndex: "actions",
        key: "actions",
        fixed: sm ? "right" : false,
        render: (_, { key, hasSender }) => (
          <div className="actions hidden gap-4">
            {hasSender && (
              <>
                <EditIcon
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    setEdittedMailId(key)
                  }}
                  className="w-6 h-6 opacity-60 cursor-pointer hover:text-primary transition-colors"
                />
                <LoginIcon
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    setReloginMailId(key)
                  }}
                  className="w-6 h-6 opacity-60 cursor-pointer hover:text-primary transition-colors"
                />
              </>
            )}
            <BinIcon
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setDeletedMailId(key)
              }}
              className="w-6 h-6 opacity-60 cursor-pointer hover:text-primary transition-colors"
            />
          </div>
        ),
        width: 136,
      })
    }
    return resultCols
  }, [pageFullAccess, sm])
  const reloginEmail = useMemo(() => {
    const mail = emails.find(({ _id }) => _id === reloginMailId)
    if (!mail) {
      return null
    }
    return {
      ...mail,
      sender: mail.sender?._id || "",
    }
  }, [emails, reloginMailId])
  const selectedEmail = emails.find(({ _id }) => _id === selectedItemId)
  return (
    <>
      <Table
        className={cx(className)}
        dataSource={dataSource}
        columns={columns}
        showSizeChanger
        onRow={({ key }) => ({
          className: "cursor-pointer",
          onClick: () => setSelectedItemId(key),
        })}
        truncate
        {...rest}
      />
      <LoginEmailModal
        data={reloginEmail}
        open={!!reloginMailId}
        onClose={() => setReloginMailId("")}
      />
      <EditEmailModal
        data={emails.find(({ _id }) => _id === editedMailId)}
        open={!!editedMailId}
        onClose={() => setEdittedMailId("")}
      />
      <DeleteEmailModal
        data={emails.find(({ _id }) => _id === deletedMailId)}
        open={!!deletedMailId}
        onClose={() => setDeletedMailId("")}
      />
      <EmailAddressDetailsDrawer
        open={!!selectedItemId}
        onClose={() => setSelectedItemId("")}
        data={selectedEmail}
        onEdit={(id) => setEdittedMailId(id)}
        showEdit={pageFullAccess && Boolean(selectedEmail?.sender)}
      />
    </>
  )
}

export default EmailAddressesTable
